import React, { useContext, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import { Ic, Logo } from './icons';
import { useTheme } from './ThemeContext';
import { Avatar } from './primitives';
import { userDisplay } from './tokens';
import NotificationBell from '../components/NotificationBell';
import PlanBadge from '../components/PlanBadge';
import SuperAdminBanner from '../components/SuperAdminBanner';
import { useWorkspace } from '../contexts/WorkspaceContext';

const BACKEND_URL = window.location.origin;

const NAV_GROUPS = [
  {
    label: 'Genel',
    items: [
      { key: 'dashboard', path: '/dashboard', icon: Ic.dashboard, label: 'Dashboard', permission: 'can_view_dashboard' },
      { key: 'tasks', path: '/tasks', icon: Ic.tasks, label: 'Görevler', permission: 'can_comment' },
      { key: 'projects', path: '/projects', icon: Ic.projects, label: 'Projeler', permission: 'can_manage_projects' },
      { key: 'teams', path: '/teams', icon: Ic.teams, label: 'Takımlar', permission: 'can_manage_teams' },
    ],
  },
  {
    label: 'Yönetim',
    items: [
      { key: 'workflows', path: '/workflows', icon: Ic.workflow, label: 'İş Akışları', permission: 'can_manage_workflows' },
      { key: 'tags', path: '/tags', icon: Ic.tag, label: 'Etiketler' },
      { key: 'files', path: '/files', icon: Ic.files, label: 'Dosyalar', permission: 'can_download_files' },
      { key: 'reports', path: '/reports', icon: Ic.reports, label: 'Raporlar', permission: 'can_view_reports' },
      { key: 'audit-log', path: '/audit-log', icon: Ic.audit, label: 'Aktivite Günlüğü', permission: 'can_manage_users' },
      { key: 'notifications', path: '/notifications/history', icon: Ic.bell, label: 'Bildirim Geçmişi', adminOnly: true },
    ],
  },
  {
    label: 'Workspace',
    items: [
      { key: 'members', path: '/settings/members', icon: Ic.users, label: 'Üyeler', workspaceAdminOnly: true },
      { key: 'billing', path: '/settings/billing', icon: Ic.settings, label: 'Faturalandırma', workspaceOwnerOnly: true },
    ],
  },
  {
    label: 'Süper Admin',
    items: [
      { key: 'sa-dashboard', path: '/super-admin', icon: Ic.dashboard, label: 'Süper Admin Panosu', superAdminOnly: true },
      { key: 'sa-users', path: '/super-admin/users', icon: Ic.users, label: 'Tüm Kullanıcılar', superAdminOnly: true },
      { key: 'sa-workspaces', path: '/super-admin/workspaces', icon: Ic.projects, label: "Tüm Workspace'ler", superAdminOnly: true },
      { key: 'sa-campaigns', path: '/super-admin/campaigns', icon: Ic.bell, label: 'E-posta Kampanyaları', superAdminOnly: true },
      { key: 'sa-history', path: '/super-admin/campaigns/history', icon: Ic.audit, label: 'Kampanya Geçmişi', superAdminOnly: true },
      { key: 'sa-audit', path: '/super-admin/audit', icon: Ic.audit, label: 'Süper Admin Günlüğü', superAdminOnly: true },
      { key: 'sa-security', path: '/security', icon: Ic.shield, label: 'Güvenlik Testi', superAdminOnly: true },
    ],
  },
];

function Sidebar({ onNavigate, mobile = false }) {
  const { user, logout } = useContext(AuthContext);
  const { activeWorkspace, isSuperAdmin, isWorkspaceOwner, isWorkspaceAdmin } = useWorkspace();
  const location = useLocation();

  const handleLogout = () => {
    if (onNavigate) onNavigate();
    if (logout) logout();
  };

  const visible = NAV_GROUPS.map(g => ({
    ...g,
    items: g.items.filter(it => {
      if (it.superAdminOnly && !isSuperAdmin) return false;
      if (it.adminOnly && user?.role !== 'admin' && !isSuperAdmin) return false;
      if (it.workspaceAdminOnly && !isWorkspaceAdmin) return false;
      if (it.workspaceOwnerOnly && !isWorkspaceOwner) return false;
      if (!it.permission) return true;
      if (isSuperAdmin) return true;
      // Workspace owner/admin için backend permissions=['all'] döner; bu
      // wildcard tüm permission gate'lerini geçmeli (backend has_permission
      // ile aynı semantik).
      const perms = user?.permissions || [];
      if (perms.includes('all')) return true;
      return perms.includes(it.permission);
    }),
  })).filter(g => g.items.length);

  // Pick the single best-matching nav item for the current path so that a
  // more-specific sub-route doesn't also light up its parent (e.g. /super-admin/users
  // shouldn't activate /super-admin's "Pano" entry).
  const activePath = (() => {
    const candidates = visible.flatMap(g => g.items.map(it => it.path));
    let best = null;
    for (const p of candidates) {
      if (location.pathname === p || location.pathname.startsWith(p + '/')) {
        if (best === null || p.length > best.length) best = p;
      }
    }
    return best;
  })();

  const isActive = (path) => path === activePath;

  const me = userDisplay({
    id: user?.id || user?.user_id || user?.email,
    name: user?.name || user?.full_name || user?.email,
    email: user?.email,
    role: user?.role,
  });

  const userPicture = user?.picture
    ? (user.picture.startsWith('http') ? user.picture : `${BACKEND_URL}${user.picture}`)
    : null;

  return (
    <aside className={`ox-surface flex flex-col shrink-0 ${mobile ? 'ox-sidebar-mobile' : ''}`}
      style={{ width: 232, borderRight: '1px solid var(--border)', height: '100vh', position: mobile ? 'relative' : 'sticky', top: 0, borderRadius: 0, borderTop: 0, borderBottom: 0, borderLeft: 0 }}>
      <div className="px-4 pt-4 pb-3 flex items-center gap-2.5" style={{ borderBottom: '1px solid var(--border)' }}>
        <Logo size={30} />
        <div className="leading-tight min-w-0">
          <div className="text-[17px] font-semibold tracking-tight">OnxFlow</div>
          <div className="text-[10.5px] ox-text-dim truncate" title={activeWorkspace?.name || 'Proje Yönetimi'}>
            {activeWorkspace?.name || 'Proje Yönetimi'}
          </div>
        </div>
        <span className="ml-auto ox-kbd shrink-0">v2.0</span>
        {mobile && onNavigate && (
          <button
            onClick={onNavigate}
            aria-label="Menüyü kapat"
            className="ox-btn ox-btn-ghost"
            style={{ padding: '4px 6px' }}
          >
            {Ic.x}
          </button>
        )}
      </div>

      <nav className="px-2 pt-3 flex-1 overflow-y-auto ox-scroll">
        {visible.map(group => (
          <React.Fragment key={group.label}>
            <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-wider ox-text-dim">{group.label}</div>
            {group.items.map(it => (
              <Link key={it.key} to={it.path}
                onClick={onNavigate}
                data-active={isActive(it.path)}
                className="ox-nav-item flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] ox-text-muted relative">
                <span className="ox-nav-bar" style={{ width: 2, height: 16, background: 'transparent', borderRadius: 2, position: 'absolute', left: -2 }} />
                <span className="ox-text-muted">{it.icon}</span>
                <span className="flex-1">{it.label}</span>
              </Link>
            ))}
          </React.Fragment>
        ))}
      </nav>

      <div className="p-3" style={{ borderTop: '1px solid var(--border)' }}>
        <Link to="/profile" onClick={onNavigate} className="flex items-center gap-2.5 p-1.5 rounded-md ox-row-hover">
          <Avatar u={me} size={28} imgSrc={userPicture} />
          <div className="flex-1 min-w-0">
            <div className="text-[12.5px] font-medium truncate">{me.name}</div>
            <div className="text-[11px] ox-text-dim">{getRoleLabel(me.role)}</div>
          </div>
          <span className="ox-text-muted">{Ic.settings}</span>
        </Link>
        <button
          type="button"
          onClick={handleLogout}
          className="w-full flex items-center gap-2.5 p-1.5 mt-1 rounded-md ox-row-hover text-[12.5px] ox-text-muted"
          title="Çıkış Yap"
        >
          <span className="ox-text-muted">{Ic.logout}</span>
          <span className="flex-1 text-left font-medium">Çıkış Yap</span>
        </button>
      </div>
    </aside>
  );
}

function getRoleLabel(role) {
  const map = {
    'admin': 'Admin',
    'proje_yoneticisi': 'Proje Yöneticisi',
    'project_manager': 'Proje Yöneticisi',
    'takim_lideri': 'Takım Lideri',
    'team_leader': 'Takım Lideri',
    'takim_uyesi': 'Takım Üyesi',
    'team_member': 'Takım Üyesi',
    'yorumcu': 'Yorumcu',
  };
  return map[String(role || '').toLowerCase()] || role || '';
}

function WorkspaceSwitcher() {
  const { workspaces, activeWorkspace, switchWorkspace } = useWorkspace();
  const [open, setOpen] = useState(false);
  if (!workspaces?.length) return null;

  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setOpen(v => !v)} className="ox-btn" title="Workspace">
        <span className="truncate" style={{ maxWidth: 140 }}>{activeWorkspace?.name || 'Workspace'}</span>
        {activeWorkspace?.plan && <PlanBadge plan={activeWorkspace.plan} />}
        {Ic.chev}
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 39 }} />
          <div className="ox-surface rounded-md" style={{
            position: 'absolute', right: 0, top: '100%', marginTop: 6, minWidth: 220, zIndex: 40,
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)', padding: 4,
          }}>
            {workspaces.map(w => (
              <button
                key={w.id}
                onClick={() => { switchWorkspace(w.id); setOpen(false); }}
                className="w-full text-left px-2.5 py-1.5 rounded-md text-[12.5px] flex items-center justify-between gap-2 ox-row-hover"
                style={{ color: w.id === activeWorkspace?.id ? 'var(--text)' : 'var(--text-muted)' }}
              >
                <span className="truncate">{w.name}</span>
                {w.plan && <PlanBadge plan={w.plan} />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Topbar({ breadcrumb = [], actions }) {
  const { theme, toggleTheme } = useTheme();
  return (
    <div className="ox-topbar"
      style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface)', position: 'sticky', top: 0, zIndex: 20 }}>
      <div className="ox-topbar-bread flex items-center gap-2 ox-text-muted min-w-0 flex-1">
        {breadcrumb.map((b, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="ox-text-dim shrink-0 text-[14px]">{Ic.chev}</span>}
            {i === breadcrumb.length - 1
              ? <span className="ox-text-body font-semibold truncate text-[22px] tracking-tight">{b.label}</span>
              : (b.to
                  ? <Link to={b.to} className="hover:text-[var(--text)] truncate ox-hide-mobile text-[13px]">{b.label}</Link>
                  : <span className="truncate ox-hide-mobile text-[13px]">{b.label}</span>)
            }
          </React.Fragment>
        ))}
      </div>
      <div className="ox-topbar-actions ml-auto flex items-center gap-2">
        {actions}
        <span className="ox-hide-on-mobile inline-flex"><WorkspaceSwitcher /></span>
        <button className="ox-btn ox-hide-on-mobile" onClick={toggleTheme} title="Tema">
          {theme === 'light' ? Ic.moon : Ic.sun}
          <span>{theme === 'light' ? 'Koyu' : 'Açık'}</span>
        </button>
        <button className="ox-btn ox-show-mobile" onClick={toggleTheme} title="Tema" aria-label="Tema değiştir" style={{ padding: '6px 8px' }}>
          {theme === 'light' ? Ic.moon : Ic.sun}
        </button>
        <div className="ox-btn !p-0 !border-0 !bg-transparent" style={{ position: 'relative' }}>
          <NotificationBell />
        </div>
      </div>
    </div>
  );
}

function WorkspaceSwitchingOverlay() {
  const { isSwitching, switchingTo, workspaces } = useWorkspace();
  if (!isSwitching) return null;
  const target = workspaces.find(w => w.id === switchingTo);
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'oxFadeIn .12s ease',
      }}
    >
      <div className="ox-surface rounded-md" style={{
        padding: '20px 24px', minWidth: 240, textAlign: 'center',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
      }}>
        <div
          className="animate-spin"
          style={{
            width: 28, height: 28, borderRadius: '50%',
            border: '3px solid var(--border-strong)',
            borderTopColor: 'var(--accent)',
          }}
        />
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
          Workspace değiştiriliyor…
        </div>
        {target?.name && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{target.name}</div>
        )}
      </div>
    </div>
  );
}

export default function Shell({ breadcrumb = [], actions, children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  // Lock body scroll while the mobile drawer is open so background doesn't bleed
  useEffect(() => {
    if (sidebarOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [sidebarOpen]);

  return (
    <div className="ox-shell flex" style={{ minHeight: '100vh' }}>
      <WorkspaceSwitchingOverlay />
      {/* Mobile sidebar handle (hidden on desktop) */}
      <div className="ox-mobile-handle">
        <button
          className="ox-btn"
          onClick={() => setSidebarOpen(v => !v)}
          aria-label={sidebarOpen ? 'Menüyü kapat' : 'Menüyü aç'}
          aria-expanded={sidebarOpen}
        >
          {sidebarOpen ? Ic.x : Ic.list}
        </button>
      </div>

      {/* Sidebar: desktop static */}
      <div className="hidden lg:block">
        <Sidebar />
      </div>

      {/* Sidebar: mobile drawer */}
      {sidebarOpen && (
        <>
          <div
            onClick={() => setSidebarOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 50 }}
            aria-hidden="true"
          />
          <div
            style={{
              position: 'fixed',
              left: 0,
              top: 0,
              bottom: 0,
              zIndex: 51,
              animation: 'oxDrawerIn .2s ease-out',
            }}
          >
            <Sidebar mobile onNavigate={() => setSidebarOpen(false)} />
          </div>
        </>
      )}

      <div className="flex-1 min-w-0">
        <Topbar breadcrumb={breadcrumb} actions={actions} />
        <SuperAdminBanner />
        <div>{children}</div>
      </div>
    </div>
  );
}
